import { createContext, useContext, useMemo } from "react";
import type { ApiFieldError } from "@/shared/types/apiError";

export interface FieldErrorContextValue {
  setFieldErrors: (formId: string, errors: ApiFieldError[]) => void;
  clearForm: (formId: string) => void;
  clearField: (formId: string, field: string) => void;
  getErrors: (formId: string) => ApiFieldError[];
  getFieldErrors: (formId: string, field: string) => ApiFieldError[];
  version: number;
}

export const FieldErrorContext = createContext<FieldErrorContextValue | null>(null);

const useFieldErrorContext = () => {
  const context = useContext(FieldErrorContext);
  if (!context) {
    throw new Error("FieldErrorProvider is missing — wrap your app root with it.");
  }
  return context;
};

export const useFormFieldErrors = (formId: string): ApiFieldError[] => {
  const context = useFieldErrorContext();
  return useMemo(() => context.getErrors(formId), [context, formId]);
};

export const useFieldError = (
  formId: string,
  field: string
): ApiFieldError[] => {
  const context = useFieldErrorContext();
  return useMemo(
    () => context.getFieldErrors(formId, field),
    [context, formId, field]
  );
};

export const useFieldErrorActions = () => {
  const { setFieldErrors, clearForm, clearField } = useFieldErrorContext();
  return { setFieldErrors, clearForm, clearField };
};
